import { Inject, Injectable } from '@angular/core';

import { EditorView } from 'prosemirror-view';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { cachedStringSearch } from '@core/symbol-data';
import { includesWithTrim } from '@core/utils-legacy';

import { MentionServiceInternal } from '../mentions-service-internal';
import { MENTION_STRATEGY_TOKEN } from '../tokens';
import type { MentionElementGroup, MentionListController, MentionsPluginEvents } from '../types';

@Injectable()
export class MentionServiceInternalGrouped extends MentionServiceInternal {
  public data: MentionElementGroup[];
  public filterData = cachedStringSearch<MentionElementGroup>(query =>
    this.data
      .map(group => ({
        ...group,
        data: (<MentionElementGroup>group).data.filter(item =>
          includesWithTrim(item.name, query, { trim: false }),
        ),
      }))
      .filter(group => group.data.length),
  );
  protected preferredPickerPositions = [
    <const>{ originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
  ];

  constructor(
    protected editorView: EditorView,
    private dataSource: Observable<MentionElementGroup[]>,
    protected eventStream$: Subject<MentionsPluginEvents>,
    private scroll$: Observable<void>,
    private destroy$: Observable<void>,
    @Inject(MENTION_STRATEGY_TOKEN)
    protected mentionListController?: MentionListController,
  ) {
    super(editorView, eventStream$, mentionListController);

    if (this.mentionListController) {
      this.mentionListController.registerRestoreFocus(() => this.editorView.focus());

      this.dataSource.pipe(takeUntil(this.destroy$)).subscribe(data => {
        this.data = data.map(group => ({
          ...group,
          data: group.data.sort((a, b) => a.name.localeCompare(b.name)),
        }));

        if (!this.mentionListController!.isOpen()) return;

        const filteredData = this.filterData(this.currentQuery);
        this.mentionListController!.setData(filteredData);
      });

      this.scroll$.pipe(takeUntil(this.destroy$)).subscribe(() => {
        if (!this.mentionListController!.isOpen()) return;

        const position = this.getPositionFromCurrentOrigin();
        this.mentionListController!.updatePosition(position);
      });
    }
  }
}
